import { useGroupMemberHook, GroupMemberHookReturn, GroupData } from './useGroupMemberHook';
import SamaritanFetch from '../../../api/httpClient';
import { Organization } from '../../../types';
import { SamaritanMemberSmallTemplate } from './useMemberSearch';

const transformOrganizationToGroupData = (organization: Organization): GroupData[] => {
    return [{
        id: organization.id,
        title: organization.name,
        tags: organization.deployments?.map(deployment => {
            return [deployment.name, 'blue'];
        }) || [],
        stats: [
            ['LIVE Members', organization.live_members_count || 0],
            ['PENDING Members', organization.pending_members_count || 0],
            ['UNENROLLED Members', organization.inactive_members_count || 0]
        ],
        startDate: new Date().toISOString(), // Placeholder since we don't have phase dates
    }];
};

const fetchOrganizationGroups = async (): Promise<Response> => {
    const response = await SamaritanFetch.v3.get('/organizations');
    
    if (!response.ok) {
        return response; // Let the error handling in useGroupMemberHook deal with it
    }
    
    const data = await response.json();
    
    // Handle different possible response structures
    const organizations: Organization[] = Array.isArray(data) ? data : data.organizations || [];
    const groups: GroupData[] = organizations.flatMap(transformOrganizationToGroupData)
        .sort((a, b) => b.id - a.id);
    
    // Override json() to return transformed data
    response.json = () => Promise.resolve({ groups });
    return response;
};

const fetchOrganizationMembers = async (groupId: number): Promise<Response> => {
    const response = await SamaritanFetch.v3.get(`/samaritan_members?template=small&samaritan_members[organization_id][]=${groupId}`);
    
    if (!response.ok) {
        return response;
    }
    
    const data = await response.json();
    
    // Transform the response to match expected format
    const members = (data.samaritan_members || []).sort((a: SamaritanMemberSmallTemplate, b: SamaritanMemberSmallTemplate) => {
        const aIsLive = a.profile_stage === 'LIVE' || a.profile_stage === 'NEEDS_REVIEW';
        const bIsLive = b.profile_stage === 'LIVE' || b.profile_stage === 'NEEDS_REVIEW';
        return aIsLive === bIsLive ? 0 : aIsLive ? -1 : 1;
    });
    
    // Override json() to return transformed data
    response.json = () => Promise.resolve({ members });
    return response;
};

export const useOrganizationMemberGroup = (): GroupMemberHookReturn => {
    return useGroupMemberHook(fetchOrganizationGroups, fetchOrganizationMembers);
};

export default useOrganizationMemberGroup; 